import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Mail, MapPin, BookOpen, GraduationCap, Shield, Rocket } from 'lucide-react';
import { Button } from '../ui/button';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '../ui/navigation-menu';
import { departments, applicationFormUrl, startups } from '../../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About BIT', path: '/about' },
    { name: 'Departments', path: '/departments', hasDropdown: true },
    { name: 'Programmes', path: '/programmes' },
    { name: 'Short Courses', path: '/short-courses' },
    { name: 'Startups', path: '/startups', hasStartupDropdown: true },
    { name: 'Admissions', path: '/admissions' },
    { name: 'Research & Innovation', path: '/research' },
    { name: 'News & Events', path: '/news' },
    { name: 'Contact', path: '/contact' },
    { name: 'BVC', path: 'https://www.bvc.ac/', isExternal: true },
  ];

  return (
    <>
      {/* Fixed Header - Transparent overlay */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? 'bg-[#142942]/95 backdrop-blur-md shadow-lg' 
            : 'bg-transparent'
        }`}
      >
        {/* Top Contact Bar */}
        <div className={`hidden md:block transition-all duration-300 ${
          isScrolled ? 'py-1 text-xs' : 'py-2 text-sm'
        } ${isScrolled ? 'bg-[#0d1f30]' : 'bg-black/30 backdrop-blur-sm'}`}>
          <div className="container-custom flex justify-between items-center text-white/90">
            <div className="flex items-center gap-6">
              <span className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#d4a855]" />
                +255 759 549 641
              </span>
              <span className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#d4a855]" />
                info@bit.ac.tz
              </span>
            </div>
            <div className="flex items-center gap-6">
              <a 
                href="https://bitlibrary.info" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-[#d4a855] transition-colors font-medium"
              >
                <BookOpen className="w-4 h-4 text-[#d4a855]" />
                BIT Library
              </a>
              <a 
                href="https://sis.bitlibrary.info/login" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-[#d4a855] transition-colors font-medium"
              >
                <GraduationCap className="w-4 h-4 text-[#d4a855]" />
                Student Portal (SIS)
              </a>
              <Link 
                to="/admin"
                className="flex items-center gap-2 hover:text-[#d4a855] transition-colors font-medium"
              >
                <Shield className="w-4 h-4 text-[#d4a855]" />
                Admin Portal
              </Link>
            </div>
          </div>
        </div>

        {/* Logo and Institution Name - Centered */}
        <div className={`transition-all duration-300 ${isScrolled ? 'py-2' : 'py-4'}`}>
          <div className="container-custom">
            <Link to="/" className="flex items-center justify-center gap-4 md:gap-6">
              {/* BIT Logo - Enlarged */}
              <img 
                src="https://customer-assets.emergentagent.com/job_bit-tanzania/artifacts/nrm99k8q_BIT%20LOGO.png" 
                alt="BIT Logo" 
                className={`object-contain transition-all duration-300 ${
                  isScrolled ? 'w-12 h-12 md:w-14 md:h-14' : 'w-16 h-16 md:w-24 md:h-24 lg:w-28 lg:h-28'
                }`}
              />
              {/* Institution Name - Large, Attractive Font */}
              <div className="text-center">
                <h1 className={`font-heading text-white tracking-wider transition-all duration-300 drop-shadow-lg ${
                  isScrolled 
                    ? 'text-xl md:text-2xl lg:text-3xl' 
                    : 'text-2xl md:text-4xl lg:text-5xl xl:text-6xl'
                }`}
                style={{ 
                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                  letterSpacing: '0.1em'
                }}>
                  BAOBAB INSTITUTE OF TANZANIA
                </h1>
                <p className={`text-[#d4a855] font-medium tracking-wide transition-all duration-300 ${
                  isScrolled ? 'text-xs md:text-sm' : 'text-sm md:text-lg'
                }`}>
                  Mapinga, Bagamoyo District, Tanzania
                </p>
              </div>
            </Link>
          </div>
        </div>

        {/* Navigation Bar */}
        <div className={`transition-all duration-300 ${
          isScrolled ? 'bg-[#1e3a5f]/90' : 'bg-black/40 backdrop-blur-sm'
        }`}>
          <div className="container-custom">
            <div className="flex items-center justify-center">
              {/* Desktop Navigation - Centered */}
              <nav className="hidden lg:flex items-center justify-center flex-1">
                <NavigationMenu>
                  <NavigationMenuList className="gap-0">
                    {navItems.map((item) => (
                      <NavigationMenuItem key={item.name}>
                        {item.hasDropdown ? (
                          <>
                            <NavigationMenuTrigger className="bg-transparent text-white hover:bg-white/10 hover:text-[#d4a855] px-5 py-4 text-base font-medium data-[state=open]:bg-white/10 data-[state=open]:text-[#d4a855]">
                              {item.name}
                            </NavigationMenuTrigger>
                            <NavigationMenuContent>
                              <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 bg-white">
                                {departments.map((dept) => (
                                  <li key={dept.id}>
                                    <NavigationMenuLink asChild>
                                      <Link
                                        to={`/departments/${dept.id}`}
                                        className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-[#f1f5f9] focus:bg-[#f1f5f9]"
                                      >
                                        <div className="text-sm font-medium text-[#1e3a5f]">
                                          {dept.shortName}
                                        </div>
                                        <p className="line-clamp-2 text-xs text-[#475569]">
                                          {dept.description}
                                        </p>
                                      </Link>
                                    </NavigationMenuLink>
                                  </li>
                                ))}
                              </ul>
                            </NavigationMenuContent>
                          </>
                        ) : item.hasStartupDropdown ? (
                          <>
                            <NavigationMenuTrigger className="bg-transparent text-white hover:bg-white/10 hover:text-[#d4a855] px-5 py-4 text-base font-medium data-[state=open]:bg-white/10 data-[state=open]:text-[#d4a855]">
                              <Rocket className="w-4 h-4 mr-1 inline" />
                              {item.name}
                            </NavigationMenuTrigger>
                            <NavigationMenuContent>
                              <div className="w-[480px] p-5 bg-gradient-to-br from-[#000a14] via-[#001a33] to-[#002952]">
                                <div className="mb-4 pb-4 border-b border-white/10">
                                  <Link to="/startups" className="flex items-center gap-3 text-white hover:text-cyan-400 transition-colors group">
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                                      <Rocket className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                      <span className="font-bold text-lg">BIT Innovation Hub</span>
                                      <p className="text-xs text-gray-400">Explore our startup ecosystem</p>
                                    </div>
                                  </Link>
                                </div>
                                <ul className="space-y-3">
                                  {startups.map((startup) => (
                                    <li key={startup.id}>
                                      <NavigationMenuLink asChild>
                                        <Link
                                          to={`/startups/${startup.id}`}
                                          className="flex items-center gap-4 p-3 rounded-xl transition-all hover:bg-white/10 group border border-transparent hover:border-white/10"
                                        >
                                          <div className="w-14 h-14 rounded-xl bg-black/40 backdrop-blur-sm p-2 flex items-center justify-center transition-transform group-hover:scale-105 border border-white/10">
                                            <img 
                                              src={startup.logo} 
                                              alt={startup.shortName}
                                              className="w-full h-full object-contain"
                                            />
                                          </div>
                                          <div className="flex-1">
                                            <div className="text-sm font-bold text-white group-hover:text-cyan-400 transition-colors">
                                              {startup.name}
                                            </div>
                                            <p className="text-xs text-gray-400 line-clamp-1 mt-0.5">
                                              {startup.tagline}
                                            </p>
                                          </div>
                                          <ChevronDown className="w-4 h-4 text-gray-500 -rotate-90 group-hover:text-cyan-400 transition-colors" />
                                        </Link>
                                      </NavigationMenuLink>
                                    </li>
                                  ))}
                                </ul>
                                <div className="mt-4 pt-4 border-t border-white/10">
                                  <Link 
                                    to="/startups" 
                                    className="flex items-center justify-center gap-2 text-sm text-cyan-400 hover:text-cyan-300 transition-colors font-medium"
                                  >
                                    View All Startups
                                    <ChevronDown className="w-4 h-4 -rotate-90" />
                                  </Link>
                                </div>
                              </div>
                            </NavigationMenuContent>
                          </>
                        ) : item.isExternal ? (
                          <a
                            href={item.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-5 py-4 text-base font-medium transition-all duration-200 block border-b-2 text-[#d4a855] hover:text-[#f4d794] border-transparent hover:border-[#d4a855]/50"
                          >
                            {item.name}
                          </a>
                        ) : (
                          <Link
                            to={item.path}
                            className={`px-5 py-4 text-base font-medium transition-all duration-200 block border-b-2 ${
                              location.pathname === item.path
                                ? 'text-[#d4a855] border-[#d4a855]'
                                : 'text-white hover:text-[#d4a855] border-transparent hover:border-[#d4a855]/50'
                            }`}
                          >
                            {item.name}
                          </Link>
                        )}
                      </NavigationMenuItem>
                    ))}
                  </NavigationMenuList>
                </NavigationMenu>

                {/* Apply Now Button - Desktop */}
                <a href={applicationFormUrl} target="_blank" rel="noopener noreferrer" className="ml-4">
                  <Button className="bg-[#d4a855] hover:bg-[#f4d794] text-[#142942] font-bold px-6 py-2 text-base rounded-full shadow-lg hover:shadow-xl transition-all">
                    Apply Now
                  </Button>
                </a>
              </nav>

              {/* Mobile Menu Toggle */}
              <div className="lg:hidden flex items-center justify-between w-full py-3">
                <span className="text-white font-semibold text-lg">Menu</span>
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="p-2 text-white hover:text-[#d4a855] transition-colors"
                  aria-label="Toggle menu"
                >
                  {mobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-[#142942]/98 backdrop-blur-md border-t border-white/10 animate-fade-in">
            <nav className="container-custom py-4">
              {/* Important Quick Links for Mobile */}
              <div className="flex gap-3 mb-4 pb-4 border-b border-white/20 flex-wrap">
                <a
                  href="https://bitlibrary.info"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 py-2 px-3 bg-[#d4a855]/20 rounded-lg text-[#d4a855] font-medium text-sm min-w-[100px]"
                >
                  <BookOpen className="w-4 h-4" />
                  Library
                </a>
                <a
                  href="https://sis.bitlibrary.info/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 py-2 px-3 bg-[#d4a855]/20 rounded-lg text-[#d4a855] font-medium text-sm min-w-[100px]"
                >
                  <GraduationCap className="w-4 h-4" />
                  SIS Portal
                </a>
                <Link
                  to="/admin"
                  className="flex-1 flex items-center justify-center gap-2 py-2 px-3 bg-[#d4a855]/20 rounded-lg text-[#d4a855] font-medium text-sm min-w-[100px]"
                >
                  <Shield className="w-4 h-4" />
                  Admin
                </Link>
              </div>
              {navItems.map((item) => (
                item.isExternal ? (
                  <a
                    key={item.name}
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block py-3 px-4 text-base font-medium border-b border-white/10 text-[#d4a855] hover:text-[#f4d794]"
                  >
                    {item.name}
                  </a>
                ) : item.hasStartupDropdown ? (
                  <div key={item.name} className="border-b border-white/10">
                    <Link
                      to={item.path}
                      className={`flex items-center gap-2 py-3 px-4 text-base font-medium ${
                        location.pathname.startsWith('/startups')
                          ? 'text-[#d4a855] bg-white/5'
                          : 'text-white hover:text-[#d4a855]'
                      }`}
                    >
                      <Rocket className="w-4 h-4 text-cyan-400" />
                      {item.name}
                    </Link>
                    <div className="pl-8 pb-3 space-y-2">
                      {startups.map((startup) => (
                        <Link
                          key={startup.id}
                          to={`/startups/${startup.id}`}
                          className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-white/5 transition-colors"
                        >
                          <div 
                            className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold text-white"
                            style={{ background: `linear-gradient(135deg, ${startup.colors.primary} 0%, ${startup.colors.secondary} 100%)` }}
                          >
                            {startup.shortName.charAt(0)}
                          </div>
                          <span className="text-sm text-gray-300 hover:text-white">{startup.shortName}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`block py-3 px-4 text-base font-medium border-b border-white/10 ${
                      location.pathname === item.path
                        ? 'text-[#d4a855] bg-white/5'
                        : 'text-white hover:text-[#d4a855]'
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              ))}
              <a
                href={applicationFormUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block mt-4"
              >
                <Button className="w-full bg-[#d4a855] hover:bg-[#f4d794] text-[#142942] font-bold text-base py-3 rounded-full">
                  Apply Now
                </Button>
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* Spacer for fixed header */}
      <div className="h-0" />
    </>
  );
};

export default Header;
